import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { hasSelectableOptions, isWeightedProduct } from 'shared/helpers/products';
import { isCartInterstitialEnabled } from '@dutchie/common/utils/discount-gamification';
import { tracker } from 'utils/analytics';
import { GTM_V2_FLAG } from 'utils/analytics/constants/feature-flags';

import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useAddToCart from 'hooks/use-add-to-cart';
import useTranslation from 'hooks/use-translation';

import {
  UseAddToCartControlsProps,
  UseAddToCartControlsReturn,
  AddToCartDataParam,
  AddToCartHandler,
} from '../add-to-cart.types';

import { usePrerequisiteChecks } from './use-prerequisite-checks';

type UseAddToCartHandlerProps = Pick<
  AddToCartDataParam,
  'additionalOption' | 'dispensary' | 'product' | 'trackerSource'
> & {
  onClose: () => void;
};

function useAddToCartHandler({
  product,
  dispensary,
  additionalOption,
  trackerSource,
  onClose,
}: UseAddToCartHandlerProps): AddToCartHandler {
  const addToCart = useAddToCart(dispensary);
  const Cart = useCart();
  const { isConciergeActive, isMobileEcommApp, openCartDrawer, openCartInterstitial, showCartDrawer } = useUI();
  const flags = useFlags();
  const shouldShowInterstitial = isCartInterstitialEnabled(dispensary);
  const isGtmV2 = !!flags[GTM_V2_FLAG];

  return async ({ option, quantity }) => {
    try {
      const { wasSuccessfullyAdded } = await addToCart({
        product,
        option,
        quantity,
        additionalOption,
        trackerSource,
      });

      onClose();
      if (wasSuccessfullyAdded) {
        if (isGtmV2) {
          Cart.addPendingAnalyticsEvent({ type: 'add', payload: { quantity, product, option, trackerSource } });
        } else {
          tracker.addedProductToCart({ quantity, product, option, trackerSource });
        }

        // Concierge owns its own post-add UX (chat-cart panel). The mobile
        // ecomm app has its own native add feedback. In both surfaces, skip
        // the marketplace cart-drawer / interstitial auto-open.
        if (isMobileEcommApp || isConciergeActive) {
          return;
        }

        // if the cart drawer is already open, we don't want to show the interstitial
        if (shouldShowInterstitial && !showCartDrawer) {
          openCartInterstitial();
        } else {
          openCartDrawer();
        }
      }
    } catch (err) {
      // user cancelled
      throw new Error(err);
    }
  };
}

export function useAddToCartControls({ data, onClose }: UseAddToCartControlsProps): UseAddToCartControlsReturn {
  const { dispensary, product, option, quantity, additionalOption, trackerSource } = data;

  const [shouldSelectProductOption, setShouldSelectProductOption] = useState(false);

  const handleAddToCart = useAddToCartHandler({ dispensary, product, additionalOption, trackerSource, onClose });

  const handlePrerequisiteChecks = usePrerequisiteChecks(data);

  const { t } = useTranslation();
  // PF-8648: apparel products with multiple size options land here too (see hasSelectableOptions
  // below) -- "Select weight" would be wrong copy for a t-shirt, so use size-specific copy for
  // any non-weighted product that reaches this picker.
  const optionSelectionLabel = isWeightedProduct(product)
    ? t('select-weight', 'Select weight')
    : t('select-size', 'Select size');
  const heading = shouldSelectProductOption ? optionSelectionLabel : '';

  useEffect(() => {
    void (async (): Promise<void> => {
      if (!(await handlePrerequisiteChecks())) {
        onClose();
        return;
      }

      // full-add
      if (option && quantity) {
        void handleAddToCart({
          option,
          quantity,
        });
      } else if (!hasSelectableOptions(product) && product.Options?.length) {
        // quick-add. hasSelectableOptions (PF-8648) covers both weighted cannabis products and
        // apparel products with multiple size options -- either way, more than one usable option
        // means we can't silently guess which one the shopper wants, so we fall through to the
        // "select product option" branch below instead.
        void handleAddToCart({
          option: product.Options[0],
          quantity: quantity ?? 1,
        });
      } else {
        // quick-add and select product options
        setShouldSelectProductOption(true);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only run once on mount

  return {
    heading,
    handleAddToCart,
    shouldSelectProductOption,
  };
}
